// © 2025 Camp Promethean, LLC. All Rights Reserved.
// Supabase Database Setup

import { supabase } from './client';

/**
 * Checks if the project_notes table exists using the database function
 */
export async function checkProjectNotesTableExists() {
  try {
    // First check if the function exists by trying to use it
    const { data, error } = await supabase
      .rpc('check_table_exists', { table_name: 'project_notes' });

    if (error) {
      // If the function doesn't exist or has a known SQL bug, try a direct query approach
      const useFallback =
        error.message.includes('function') ||
        error.message.includes('does not exist') ||
        error.message.includes('ambiguous');
      if (useFallback) {
        console.warn('Function check_table_exists unavailable, using fallback query');

        // Fallback: Try to query the table directly to see if it exists
        try {
          const { data: fallbackData, error: fallbackError } = await supabase
            .from('project_notes')
            .select('id', { count: 'exact', head: true });

          if (fallbackError) {
            // Table doesn't exist or we don't have access
            return false;
          }

          return true; // Table exists
        } catch (fallbackError) {
          console.warn('Fallback query failed:', fallbackError);
          return null;
        }
      }

      console.warn('Function check_table_exists failed:', error.message);
      return null;
    }

    return !!data;
  } catch (error) {
    console.error('Error in database check:', error);
    return null;
  }
}

/**
 * Creates the project_notes table if it doesn't exist
 */
export async function createProjectNotesTable() {
  try {
    // Use the create_project_notes_table function
    const { data, error } = await supabase
      .rpc('create_project_notes_table');

    if (error) {
      // If the function doesn't exist, try to create the table directly
      if (error.message.includes('function') || error.message.includes('does not exist')) {
        console.warn('Function create_project_notes_table does not exist, using fallback SQL');

        // Fallback: Try to create the table directly using SQL
        try {
          // Note: Direct table creation via RPC might not work due to permissions
          // This is primarily for development environments
          console.warn('Table creation requires database migration. Please run: supabase db push');
          return false;
        } catch (fallbackError) {
          console.error('Fallback table creation failed:', fallbackError);
          return false;
        }
      }

      console.error('Error creating project_notes table:', error);
      return false;
    }

    console.log('Project notes table created or already exists');
    return true;
  } catch (error) {
    console.error('Error creating project_notes table:', error);
    return false;
  }
}

/**
 * Call this function when the app initializes
 */
export async function setupDatabase() {
  try {
    // First check if stored procedures exist by checking the table
    const tableExists = await checkProjectNotesTableExists();
    
    // Only try to create if we can confirm it doesn't exist
    if (tableExists === false) {
      // If we know the table doesn't exist, try to create it
      console.log('Project notes table does not exist, attempting to create it...');
      const created = await createProjectNotesTable();
      
      if (!created) {
        console.warn(
          'Could not create project_notes table. ' +
          'Make sure migrations have been applied. ' +
          'See supabase/migrations/README.md for instructions.'
        );
      }
    } else if (tableExists === null) {
      // If we can't check properly, just log a warning
      console.warn(
        'Could not verify if project_notes table exists. ' +
        'This is expected in development environments without migrations. ' +
        'The application will continue to function normally.'
      );
    } else {
      console.log('Project notes table exists');
    }
  } catch (error) {
    console.error('Database setup error:', error);
    // Continue application execution despite setup errors
  }
} 